import React from 'react';
import { Card, Badge } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { Calculator, Tags, Database, LayoutTemplate, FileSpreadsheet, LineChart, ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export function EstimatesModulesGrid() {
  const navigate = useNavigate();

  const MODULES = [
    {
      id: 'list',
      title: 'Hồ sơ Dự toán',
      description: 'Quản lý danh sách dự toán, bóc tách khối lượng và lập báo giá cho khách hàng.',
      icon: <Calculator className="w-6 h-6" />,
      color: 'blue',
      path: '/estimates/list',
      stats: '145 Hồ sơ',
      gradient: 'from-blue-500 to-cyan-500',
      bgLight: 'bg-blue-50',
      textDark: 'text-blue-700'
    },
    {
      id: 'norms',
      title: 'Định mức Hao phí',
      description: 'Thiết lập định mức nhân công, vật tư, máy móc cho từng công tác cảnh quan chuẩn.',
      icon: <Tags className="w-6 h-6" />,
      color: 'amber',
      path: '/estimates/norms',
      stats: 'Master Data',
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'bg-amber-50',
      textDark: 'text-amber-700'
    },
    {
      id: 'rates',
      title: 'Đơn giá Tài nguyên',
      description: 'Cập nhật thư viện báo giá vật tư, lương nhân công và giá thuê thiết bị thị trường.',
      icon: <Database className="w-6 h-6" />,
      color: 'emerald',
      path: '/estimates/rates',
      stats: 'Bảng giá',
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'bg-emerald-50',
      textDark: 'text-emerald-700'
    },
    {
      id: 'templates',
      title: 'Mẫu Quy trình',
      description: 'Các gói công việc mẫu (Process Templates) giúp lập dự toán nhanh chóng.',
      icon: <LayoutTemplate className="w-6 h-6" />,
      color: 'purple',
      path: '/estimates/process-templates',
      stats: 'Templates',
      gradient: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50',
      textDark: 'text-purple-700'
    },
    {
      id: 'boq',
      title: 'Import BOQ',
      description: 'Đọc và bóc tách dữ liệu BOQ từ file Excel, PDF của Chủ đầu tư/Tư vấn.',
      icon: <FileSpreadsheet className="w-6 h-6" />,
      color: 'slate',
      path: '/construction/boq',
      stats: 'Tích hợp',
      gradient: 'from-slate-500 to-gray-600',
      bgLight: 'bg-slate-100',
      textDark: 'text-slate-700'
    },
    {
      id: 'profitability',
      title: 'Phân tích Biên lãi',
      description: 'Mô phỏng doanh thu, chi phí trực tiếp và tính toán tỷ suất lợi nhuận (Gross Margin).',
      icon: <LineChart className="w-6 h-6" />,
      color: 'indigo',
      path: '/reports/financial',
      stats: 'Analytics',
      gradient: 'from-indigo-500 to-violet-500',
      bgLight: 'bg-indigo-50',
      textDark: 'text-indigo-700'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      {MODULES.map((mod) => (
        <Card
          key={mod.id}
          onClick={() => navigate(mod.path)}
          className="group relative overflow-hidden bg-white border-2 border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl rounded-3xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
        >
          <div className={cn("absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-bl-full transition-transform duration-500 group-hover:scale-110", mod.gradient)} />

          <div className="flex items-start justify-between mb-4">
            <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3", mod.bgLight, mod.textDark)}>
              {mod.icon}
            </div>
            <Badge variant="custom" className={cn("bg-slate-50 text-slate-600 border border-slate-100 font-bold px-2.5 py-1 text-[10px]")}>
              {mod.stats}
            </Badge>
          </div>

          <h4 className="text-lg font-black text-slate-800 mb-2 group-hover:text-blue-700 transition-colors">{mod.title}</h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6 line-clamp-2 min-h-[36px] flex-grow">
            {mod.description}
          </p>

          <div className="flex items-center text-sm font-bold text-blue-600 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-auto">
            Truy cập ngay <ArrowRight className="w-4 h-4 ml-1.5" />
          </div>
        </Card>
      ))}
    </div>
  );
}
